import Delegate from './delegate'
import Session from './session'
import Speaker from './speaker'
import Opinion from '../enums/opinion'
import MotionState from '../enums/motion-state'
import SpeakerState from '../enums/speaker-state'

export default class Motion {
  constructor (id, name) {
    this.id = id
    this.uuid = null
    this.name = name
    this.num = null
    this.amendments = []
    this.state = MotionState.PENDING
    this.stateReason = null
    this.order = 0
    this.proposer = null
    this.proposerState = null
    this.seconder = null
    this.seconderState = null
    this.session = null
    this.isAmendment = false
    this.parent = null
    this.for = []
    this.against = []
    this.speakers = []
    this.allSpeakers = []
    this.next = null
  }

  static fromJson (json) {
    let motion = new this()
    if (json.id !== null) {
      motion.id = json.id
    }
    if (json.uuid !== null) {
      motion.uuid = json.uuid
    }
    if (json.name !== null) {
      motion.name = json.name
    }
    if (json.state !== null) {
      motion.state = json.state
    }
    if (json.state_reason !== null) {
      motion.stateReason = json.state_reason
    }
    if (json.order !== null) {
      motion.order = json.order
    }
    if (json.num !== null) {
      motion.num = json.num
    }
    if (json.hasOwnProperty('proposer') && json.proposer !== null) {
      motion.proposer = Delegate.fromJson(json.proposer)
    }
    if (json.proposer_state !== null) {
      motion.proposerState = json.proposer_state
    }
    if (json.hasOwnProperty('seconder') && json.seconder !== null) {
      motion.seconder = Delegate.fromJson(json.seconder)
    }
    if (json.seconder_state !== null) {
      motion.seconderState = json.seconder_state
    }
    if (json.hasOwnProperty('session') && json.session !== null) {
      motion.session = Session.fromJson(json.session)
    }
    if (json.children !== null && json.children !== undefined) {
      motion.amendments = json.children.map((d) => {
        let amendment = this.fromJson(d)
        // amendment.parent = motion
        return amendment
      })
    }
    if (json.parent_id !== null) {
      motion.isAmendment = true
    }
    if (json.parent !== null && json.parent !== undefined) {
      motion.parent = Motion.fromJson(json.parent)
    }
    if (json.speakers !== null && json.speakers !== undefined) {
      const speakers = json.speakers.map((d) => Speaker.fromJson(d))
      motion.for = speakers
        .filter((speaker) => speaker.opinion === Opinion.FOR)
        .sort((a, b) => a.order - b.order)
      motion.against = speakers
        .filter((speaker) => speaker.opinion === Opinion.AGAINST)
        .sort((a, b) => a.order - b.order)
      motion.speakers = speakers
        .sort((a, b) => a.order - b.order)
    }
    return motion
  }

  static fromCurrentJson (json) {
    let motion = new this()
    motion.state = MotionState.ACTIVE
    if (json.motion !== null) {
      if (json.motion.id !== null) {
        motion.id = json.motion.id
      }
      if (json.motion.uuid !== null) {
        motion.uuid = json.motion.uuid
      }
      if (json.motion.name !== null) {
        motion.name = json.motion.name
      }
      if (json.motion.number !== null) {
        motion.number = json.motion.number
      }
      if (json.motion.proposer !== null) {
        motion.proposer = new Delegate()
        motion.proposer.name = json.motion.proposer
        if (json.motion.proposer_id !== null) {
          motion.proposer.id = json.motion.proposer_id
          if (json.motion.proposer_association !== null) {
            motion.proposer.associationName = json.motion.proposer_association
          }
        }
        if (json.motion.proposer_state !== null) {
          motion.proposerState = json.motion.proposer_state
        }
      }
      if (json.motion.seconder !== null) {
        motion.seconder = new Delegate()
        motion.seconder.name = json.motion.seconder
        if (json.motion.seconder_id !== null) {
          motion.seconder.id = json.motion.seconder_id
          if (json.motion.seconder_association !== null) {
            motion.seconder.associationName = json.motion.seconder_association
          }
        }
        if (json.motion.seconder_state !== null) {
          motion.seconderState = json.motion.seconder_state
        }
      }
      if (json.next !== null) {
        console.log('found next')
        motion.next = new this()
        if (json.next.name !== null) {
          motion.next.name = json.next.name
          console.log(motion.next.name)
        }
        if (json.next.proposer !== null) {
          motion.next.proposer = new Delegate()
          motion.next.proposer.name = json.next.proposer
        }
        if (json.next.proposer_association !== null) {
          motion.next.proposer.associationName = json.next.proposer_association
        }
        if (json.next.seconder !== null) {
          motion.next.seconder = new Delegate()
          motion.next.seconder.name = json.next.seconder
        }
        if (json.next.seconder_association !== null) {
          motion.next.seconder.associationName = json.next.seconder_association
        }
      } else {
        console.log('there is no next motion')
      }
    }
    if (json.for !== null) {
      motion.for = json.for.map((data) => Speaker.fromJson(
        Object.assign({}, data, {
          delegate: {
            full_name: data.full_name,
            association_name: data.association_name,
            real_person: data.real_person
          },
          opinion: Opinion.FOR
        }))
      ).sort((a, b) => a.order - b.order)
    }
    if (json.against !== null) {
      motion.against = json.against.map((data) => Speaker.fromJson(
        Object.assign({}, data, {
          delegate: {
            full_name: data.full_name,
            association_name: data.association_name,
            real_person: data.real_person
          },
          opinion: Opinion.AGAINST
        }))
      ).sort((a, b) => a.order - b.order)
    }
    motion.speakers = motion.for.concat(motion.against).sort((a, b) => a.order - b.order)
    return motion
  }

  addAmendnment (amendment) {
    this.amendments.push(amendment)
  }

  hasAmendments () {
    return this.amendments.length > 0
  }

  hasParent () {
    return this.parent !== null && this.parent !== undefined
  }

  hasSession () {
    return this.session !== null
  }

  hasProposer () {
    return this.proposer !== null
  }

  hasSeconder () {
    return this.seconder !== null
  }

  isActive () {
    return this.state === MotionState.ACTIVE
  }

  speakerSpeaking () {
    return this.for.filter((speaker) => speaker.status === SpeakerState.ACTIVE).length > 0 ||
      this.against.filter((speaker) => speaker.status === SpeakerState.ACTIVE).length > 0
  }

  propSpeaking () {
    return this.proposerState === SpeakerState.ACTIVE || this.seconderState === SpeakerState.ACTIVE
  }

  anyoneSpeaking () {
    return this.speakerSpeaking() || this.propSpeaking()
  }

  speaking () {
    if (this.proposerState === SpeakerState.ACTIVE) {
      return {
        delegate: this.proposer,
        type: 'Proposer'
      }
    }
    if (this.seconderState === SpeakerState.ACTIVE) {
      return {
        delegate: this.seconder,
        type: 'Seconder'
      }
    }
    const speakersFor = this.for.filter((speaker) => speaker.status === SpeakerState.ACTIVE)
    if (speakersFor.length) {
      return {
        delegate: speakersFor[0].delegate,
        type: 'Delegate'
      }
    }
    const speakersAgainst = this.for.filter((speaker) => speaker.status === SpeakerState.ACTIVE)
    if (speakersAgainst.length) {
      return {
        delegate: speakersAgainst[0].delegate,
        type: 'Delegate'
      }
    }
    return null
  }

  nameFormatted () {
    if (this.isAmendment) {
      return this.name
    }
    console.log(this)
    console.log('not amendment, proceed')
    console.log('number is', this.number)
    if (this.number !== null && this.number !== undefined && this.number.length > 0) {
      return `${this.number}. ${this.name}`
    }
    console.log('no number, proceed')
    return this.name
  }
}



// WEBPACK FOOTER //
// ./src/models/motion.js